import {
  setBreederId,
  useApplicationExistsModal,
  useApplicationMessageModal,
  useAuthModal,
} from '@application/components';
import { useGetConversations } from '@application/components/controllers/pages/MtMessagesPageController/api/useGetConversations';
import {
  formatLanguages,
  formatLocation,
  formatPuppiesSold,
  readableDate,
  useRouter,
} from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import type { BreederCardInfoFragment, BreederGetBreederQuery } from '@gql/graphql';
import { CountryCode } from '@gql/graphql';
import type { ContactInfoData } from '@presentation/components';
import dayjs from 'dayjs';
import { useGetApplicationExistsForBreederId } from '@application/api';
import { useCallback, useMemo } from 'react';
import { useCurrentAccount } from '../account/useCurrentAccount';

type Breeder = NonNullable<BreederGetBreederQuery['findManyDogBreeder']>[number];

type BreederInput = Pick<
  Breeder,
  | 'reviews'
  | 'agreementNumber'
  | 'level'
  | 'certificationFile'
  | 'profileImg'
  | 'spokenLanguages'
  | 'lastConnectedType'
  | 'id'
  | 'breedingName'
  | 'account'
  | 'translations'
  | 'applications'
> & { translations: Pick<BreederCardInfoFragment['translations'][0], 'url'>[] } & {
  account: {
    connectedAt: Breeder['account']['connectedAt'];
    firstName: Breeder['account']['firstName'];
    lastName: Breeder['account']['lastName'];
    region: NonNullable<Breeder['account']['address']>['region'];
    address: Breeder['account']['address'];
  };
};

export const useFormatBreederContactInfo = (breeder?: BreederInput | null) => {
  const { translate, language, navigateToRoute, infoFromCurrentPath } = useRouter();
  const { show: showAuthModal } = useAuthModal();
  const { modalHook: applicationExistsModalHook } = useApplicationExistsModal();
  const { currentAccountLoggedIn, currentAccount, currentAccountType } = useCurrentAccount();
  const { show, setAccountId } = useApplicationMessageModal();
  const { conversations } = useGetConversations();
  const {
    application: applicationForBreederId,
    applicationLoading: applicationForBreederIdLoading,
  } = useGetApplicationExistsForBreederId(breeder?.id);

  const existingConversationId: number | undefined = useMemo(() => {
    return conversations.find((x) => x.otherAccountId === breeder?.id)?.id;
  }, [breeder?.id, conversations]);

  const writeReviewClickHandler = useCallback(() => {
    if (!currentAccountLoggedIn) {
      showAuthModal();
      return;
    }

    navigateToRoute('breederReview', {
      params: [breeder?.translations.find((x) => x.language === language)?.url as string],
    });
  }, [breeder?.translations, currentAccountLoggedIn, language, navigateToRoute, showAuthModal]);

  const applyClickHandler = useCallback(
    (event: any) => {
      if (!breeder || applicationForBreederIdLoading) return;

      if (applicationForBreederId) {
        setBreederId(breeder?.id);
        applicationExistsModalHook.show();
        return;
      }

      Analytics.sendApplyEvent(event.target.dataset.from);
      navigateToRoute('application', {
        params: [breeder.id.toString()],
      });
    },
    [
      applicationExistsModalHook,
      applicationForBreederId,
      applicationForBreederIdLoading,
      breeder,
      navigateToRoute,
    ],
  );

  const sendMessageClickHandler = useCallback(() => {
    if (!breeder?.id) {
      return;
    }

    if (
      !currentAccountLoggedIn ||
      currentAccountType === 'buyer' ||
      !currentAccount?.dogBreeder?.certified
    ) {
      showAuthModal();
      return;
    }

    if (existingConversationId) {
      navigateToRoute('mtMessages', { params: [breeder.id.toString()] });
    } else {
      setAccountId(breeder.id);
      show();
    }
  }, [
    breeder?.id,
    currentAccount?.dogBreeder?.certified,
    currentAccountLoggedIn,
    currentAccountType,
    existingConversationId,
    navigateToRoute,
    setAccountId,
    show,
    showAuthModal,
  ]);

  const showReviewButton = useMemo(() => {
    if (currentAccount?.id === breeder?.id) return false;
    if (currentAccount?.id && breeder?.reviews?.find((x) => x.accountId === currentAccount?.id))
      return false;
    return true;
  }, [breeder?.id, breeder?.reviews, currentAccount?.id]);

  const showSendMessageButton = useMemo(() => {
    if (currentAccount?.id === breeder?.id) return false;
    return true;
  }, [breeder?.id, currentAccount?.id]);

  const showCertificationLabel = useMemo(() => {
    const country = breeder?.account?.address?.country ?? CountryCode.BE;
    const level = breeder?.level ?? '';

    return (
      breeder?.certificationFile &&
      [CountryCode.FR, CountryCode.NL].includes(country) &&
      ['3', '6'].includes(level)
    );
  }, [breeder?.account?.address?.country, breeder?.level, breeder?.certificationFile]);

  const contactInfo = useMemo((): ContactInfoData | undefined => {
    if (breeder == null) return;
    const showBreederDetails =
      currentAccountType === 'breeder' &&
      currentAccount?.dogBreeder?.certified &&
      infoFromCurrentPath.route === 'stud';

    const showBreederLevel = breeder.account?.address?.country !== CountryCode.CH;

    return {
      profileImage: breeder.profileImg ?? undefined,
      values: [
        ...(showBreederDetails
          ? [
              {
                label: translate('glossary.breederName'),
                value: breeder.account.firstName,
              },
              {
                label: translate('glossary.breedingName'),
                value: breeder.breedingName,
              },
            ]
          : []),
        ...(dayjs(breeder.account.connectedAt).isValid()
          ? [
              {
                label: translate('glossary.lastConnected'),
                value: readableDate(
                  dayjs.duration(dayjs(breeder.account.connectedAt).diff(dayjs())),
                  translate,
                ),
              },
            ]
          : []),
        ...(breeder.agreementNumber
          ? [
              {
                label: translate(`glossary.agreementNumber.${breeder.account?.address?.country}`),
                value: translate('glossary.verified'),
              },
            ]
          : []),
        ...(showBreederLevel
          ? [
              {
                label: translate('glossary.breederType'),
                value: ['1', '2', '3', '4', '5', '6', '7', '8'].includes(breeder.level ?? '')
                  ? translate(`common.options.breederTypes.${breeder.level}`)
                  : undefined,
              },
            ]
          : []),
        ...(showCertificationLabel
          ? [
              {
                label: translate('glossary.certificationFile'),
                value: translate('glossary.given'),
              },
            ]
          : []),
        {
          label: translate('glossary.spokenLanguages'),
          value: formatLanguages(language, breeder?.spokenLanguages ?? []),
        },
        ...(breeder?.applications?.length > 0
          ? [
              {
                label: translate('glossary.adoptedPuppies'),
                value: formatPuppiesSold(breeder?.applications.length),
              },
            ]
          : []),
        {
          label: translate('glossary.location'),
          value: formatLocation(
            {
              country: breeder.account?.address?.country,
              region: breeder.account?.address?.region,
            },
            translate,
          ),
        },
      ],
      onClick: () =>
        navigateToRoute('breeder', {
          params: [breeder?.translations?.[0]?.url ?? ''],
        }),
      onWriteReviewClick: writeReviewClickHandler,
      onApplyClick: applyClickHandler,
      onSendMessageClick: sendMessageClickHandler, // breeder to breeder
      showReviewButton,
      showSendMessageButton,
      lastConnectedType: breeder.lastConnectedType ?? undefined,
    };
  }, [
    breeder,
    currentAccountType,
    currentAccount?.dogBreeder?.certified,
    infoFromCurrentPath.route,
    translate,
    showCertificationLabel,
    language,
    writeReviewClickHandler,
    applyClickHandler,
    sendMessageClickHandler,
    showReviewButton,
    showSendMessageButton,
    navigateToRoute,
  ]);

  return { contactInfo };
};
